import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { match, none, Option, some } from '../../../../shared/functions/functional';
import { FieldTree, FormField, SchemaPathTree } from '@angular/forms/signals';
import { Fuse_IEC60269Dto, InDto, MceApiService } from '../../../../core/mceapi.client.generated';
import { Load } from '../../load/load.model';
import { rxResource } from '@angular/core/rxjs-interop';
import { of } from 'rxjs';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSelectModule } from '@angular/material/select';

export interface FuseIEC60269 {
  assemblyGuid: string,
  rating: number,
  autoRating: boolean,
}

export function buildFuseIEC60269Schema(path: SchemaPathTree<FuseIEC60269>) { }

export function createFuseIEC60269(dto: Option<Fuse_IEC60269Dto>) {
  return signal<FuseIEC60269>(
    match(
      () => ({ assemblyGuid: '', rating: 0, autoRating: true }),
      (x: Fuse_IEC60269Dto): FuseIEC60269 => {
        if (x.assemblyGuid === undefined || x.rating === undefined || x.autoRating === undefined) {
          throw new Error('IEC 60269 fuse DTO is missing required values.');
        }

        return {
          assemblyGuid: x.assemblyGuid,
          rating: x.rating,
          autoRating: x.autoRating,
        };
      })(dto));
}

export function createFuseIEC60269Dto(model: FieldTree<FuseIEC60269>) {
  return signal<Option<Fuse_IEC60269Dto>>(
    model.assemblyGuid().value() === '' || model.rating().value() === 0
      ? none
      : some({
        assemblyGuid: model.assemblyGuid().value(),
        rating: model.rating().value(),
        autoRating: model.autoRating().value(),
      }));
}

@Component({
  selector: 'protection-fuse-iec60269',
  imports: [FormField, MatSelectModule, MatSlideToggleModule],
  templateUrl: './fuse-iec60269.component.html',
})
export class FuseIec60269Component {

  readonly form = input.required<FieldTree<FuseIEC60269>>();
  readonly load = input.required<FieldTree<Load>>();

  private readonly mceApiService = inject(MceApiService);

  framesRx = rxResource({
    params: () => ({}),
    stream: () =>
      this.mceApiService.v1ProtectionFrames('Fuse_IEC60269')
  });
  frames = computed(() => this.framesRx.value() ?? []);

  ratingsRx = rxResource({
    params: () => ({ assemblyGuid: this.form().assemblyGuid().value() }),
    stream: ({ params }) =>
      params.assemblyGuid === ''
        ? of([])
        : this.mceApiService.v1ProtectionRatings('Fuse_IEC60269', params.assemblyGuid)
  });
  ratings = computed(() => this.ratingsRx.value() ?? []);

  autoRatingRx = rxResource({
    params: () => ({ assemblyGuid: this.form().assemblyGuid().value(), ib: this.load().ib().value() }),
    stream: ({ params }) =>
      params.assemblyGuid === ''
        ? of({ i: 0 })
        : this.mceApiService.v1ProtectionRatingsAuto('Fuse_IEC60269', params.assemblyGuid, params.ib)
  });
  autoRating = computed<InDto>(() => this.autoRatingRx.value() ?? { i: 0 });

  setAutoRating = effect(() => {
    if (!this.form().autoRating().value()) return;
    const autoRating = this.autoRating().i;
    if (autoRating === undefined || autoRating === 0) return;

    if (autoRating !== this.form().rating().value())
      this.form().rating().value.set(autoRating);
  });


  /// Ensure the frames has a valid selection.
  frameToValid = effect(() => {
    const frameGuid = this.frames()[0]?.value;
    if (frameGuid !== undefined && this.form().assemblyGuid().value() === '')
      this.form().assemblyGuid().value.set(frameGuid);
  });

}
